
export class MigrationSafe<T> {
  static readonly UNDEFINED_STATUS: string = '__UNDEFINED__';

  private constructor(
    public readonly isDefined: boolean,
    public readonly isSome: boolean,
    private readonly _value: T | null = null,
  ) {}

  static some<T>(value: T): MigrationSafe<T> {
    return new MigrationSafe(true, true, value);
  }

  static defined<T>(value?: T | null): MigrationSafe<T> {
    return value == null ? new MigrationSafe(true, false) : new MigrationSafe(true, true, value);
  }

  static undefined<T>(): MigrationSafe<T> {
    return new MigrationSafe(false, false);
  }

  get isUndefined(): boolean {
    return !this.isDefined;
  }

  get isNone(): boolean {
    return !this.isSome;
  }

  get value(): T {
    if(this.isUndefined) {
      throw new Error('value is not accessible on an undefined instance');
    }
    if(this.isNone) {
      throw new Error('value is not accessible on a none instance');
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this._value!;
  }

  get valueOrNull(): T | null {
    if(this.isUndefined) {
      throw new Error('value is not accessible on an undefined instance');
    }

    return this._value;
  }

  getOrElse(defaultValue: T): T {
    return this.isSome ? this.value : defaultValue;
  }

  encode<R>(encoder: (value: T) => R): unknown {
    if(this.isSome) {
      return encoder(this.value);
    }
    else if(this.isDefined) {
      return null;
    }
    else {
      return MigrationSafe.UNDEFINED_STATUS;
    }
  }

  clone(cloner: (value: T) => T): MigrationSafe<T> {
    if(this.isSome) {
      return MigrationSafe.some(cloner(this.value));
    }
    else if(this.isDefined) {
      return MigrationSafe.defined<T>();
    }
    else {
      return MigrationSafe.undefined<T>();
    }
  }

  isSame(other: MigrationSafe<T>, comparator?: (other: T, self: T) => boolean): boolean {
    if(other.isSome && this.isSome) {
      return comparator != null ? comparator(other.value, this.value) : (other.value === this.value);
    }
    else if(other.isDefined === this.isDefined) {
      return true;
    }

    return false;
  }

  static decode<T, R>(rawData: unknown, decoder: (data: R) => T): MigrationSafe<T> {
    if(rawData == MigrationSafe.UNDEFINED_STATUS) {
      return MigrationSafe.undefined<T>();
    }
    else if(rawData == null) {
      return MigrationSafe.defined<T>(null);
    }
    else {
      return MigrationSafe.some(decoder(rawData as R));
    }
  }
}
